// Export Sage — génération d'exports comptables au format Sage
import { useState, useCallback, useMemo } from "react";
import {
  Download,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Clock,
  XCircle,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useSageExports, useTriggerSageExport } from "@/hooks/admin/useFinance";
import type { SageExportJob, SageExportStatus } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<SageExportStatus, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  processing: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABELS: Record<SageExportStatus, string> = {
  pending: "En attente",
  processing: "En cours",
  completed: "Terminé",
  failed: "Échec",
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

function formatDateTime(d: string): string {
  return new Date(d).toLocaleString("fr-SN");
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SageExport() {
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const { data, isLoading, isError, refetch } = useSageExports();
  const triggerExport = useTriggerSageExport();

  const jobs = data?.jobs ?? [];

  const handleTrigger = useCallback(() => {
    if (!period) return;
    triggerExport.mutate(period);
  }, [period, triggerExport]);

  const columns: readonly ColumnDef<SageExportJob>[] = useMemo(
    () => [
      {
        id: "period",
        header: "Période",
        cell: (job) => (
          <span className="text-sm font-medium text-stone-900">{job.period}</span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (job) => (
          <div className="flex items-center gap-2">
            {job.status === "processing" && (
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            )}
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[job.status]}`}
            >
              {STATUS_LABELS[job.status]}
            </span>
          </div>
        ),
      },
      {
        id: "created_at",
        header: "Créé le",
        cell: (job) => (
          <span className="text-sm text-stone-500">
            {formatDateTime(job.created_at)}
          </span>
        ),
      },
      {
        id: "completed_at",
        header: "Terminé le",
        cell: (job) => (
          <span className="text-sm text-stone-500">
            {job.completed_at ? formatDate(job.completed_at) : "—"}
          </span>
        ),
      },
      {
        id: "file",
        header: "Fichier",
        cell: (job) => {
          if (job.status === "completed" && job.file_url) {
            return (
              <a
                href={job.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
              >
                <Download className="h-3.5 w-3.5" />
                Télécharger
              </a>
            );
          }
          if (job.status === "failed" && job.error) {
            return (
              <span
                className="max-w-xs truncate text-xs text-red-600"
                title={job.error}
              >
                {job.error}
              </span>
            );
          }
          return <span className="text-xs text-stone-400">—</span>;
        },
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Download className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Export Sage
            </h1>
            <p className="text-sm text-stone-500">
              Exports comptables pour synchronisation avec Sage
            </p>
          </div>
        </div>

        {/* Explanation card */}
        <div className="rounded-lg border border-stone-200 bg-stone-50 px-5 py-4">
          <p className="text-sm text-stone-600">
            Générez un export comptable au format Sage pour synchronisation avec
            votre logiciel de comptabilité. Chaque export contient toutes les
            écritures comptables de la période sélectionnée.
          </p>
        </div>

        {/* Generate panel */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-stone-700">
              Période (AAAA-MM) :
            </label>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <Button
            onClick={handleTrigger}
            disabled={triggerExport.isPending || !period}
            className="gap-1.5"
          >
            {triggerExport.isPending ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Générer l'export
          </Button>
        </div>

        {/* Feedback */}
        {triggerExport.isSuccess && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>Export lancé. Il apparaîtra dans le tableau ci-dessous.</span>
          </div>
        )}
        {triggerExport.isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de lancer l'export Sage.</span>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500">
          <span className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-amber-500" />
            En attente
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            En traitement
          </span>
          <span className="flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
            Terminé — lien de téléchargement disponible
          </span>
          <span className="flex items-center gap-1.5">
            <XCircle className="h-3.5 w-3.5 text-red-500" />
            Échec — message d'erreur
          </span>
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les exports Sage.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Recent exports table */}
        {!isError && (
          <>
            <h2 className="text-sm font-semibold text-stone-700">
              Exports récents
            </h2>
            {!isLoading && jobs.length === 0 ? (
              <EmptyState
                icon={<Download className="h-6 w-6" />}
                title="Aucun export"
                description="Générez votre premier export Sage ci-dessus."
              />
            ) : (
              <AdminDataTable
                data={jobs}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
